import { EventType } from '@oneclick/analytics-sdk';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useAnalytics from '../../../../hooks/useAnalytics';
import useStudentRecommendations from '../../../hooks/useStudentRecommendations';
import ChatButton from '../../atoms/ChatButton/ChatButton';
import RecommendationCard from './RecommendationCard';
import './StudentRecommendations.scss';

function StudentRecommendations({ className }) {
  const { t } = useTranslation(['chatbot']);
  const { generateSessions, isGenerating, isInitialLoading, recommendations } = useStudentRecommendations();
  const history = useHistory();
  const cardsContainerRef = useRef(null);
  const { trackEvent } = useAnalytics();

  const handleRecommendationClick = (recommendation) => {
    // Navigate directly using the session data that's already available
    const { sessionData } = recommendation;

    if (sessionData && sessionData.activityInstances && sessionData.activityInstances.length > 0) {
      // Send analytics event for activity session started
      trackEvent(EventType.ACTIVITY_SESSION_STARTED, {
        practice_session_id: sessionData.id,
        type: recommendation.challengeType, // "new", "review", etc.
        subject: sessionData.subject, // "Mathematics", "Language", etc.
      });

      // Get the last activity instance (highest index/order)
      const lastActivity = sessionData.activityInstances[sessionData.activityInstances.length - 1];

      // Navigate using the standard template literal pattern
      history.push(`/activities/sessions/${sessionData.id}/activities/${lastActivity.id}`);
    } else {
      console.error('No activity instances found for session:', recommendation.id);
    }
  };

  const isEmptyState = !isGenerating && !isInitialLoading && !recommendations.length;

  return (
    <div className={clsx('student-recommendations', className)}>
      <div className="student-recommendations__header">
        <div className="student-recommendations__header-left">
          <div className="student-recommendations__avatar" />
          <div className="student-recommendations__header-content">
            <h2 className="student-recommendations__title">{t('Recommendations')}</h2>
            {recommendations.length > 0 ? (
              <p className="student-recommendations__subtitle">{t('Based on your performance', { count: recommendations.length })}</p>
            ) : (
              isEmptyState && (
                <>
                  <p className="student-recommendations__subtitle">{t('Great job!')}</p>
                  <p className="student-recommendations__subtitle">{t('Tomorrow will have new sessions ready for you.')}</p>
                </>
              )
            )}
          </div>
        </div>

        <ChatButton onClick={generateSessions} disabled={isGenerating} variant="outlined" size="large">
          {t(isEmptyState ? 'I want to keep practicing' : 'Generate more')}
        </ChatButton>
      </div>

      {(isGenerating || isInitialLoading) && (
        <div className="student-recommendations__cards">
          <div className="student-recommendations__loader">{t('Loading')}...</div>
        </div>
      )}

      {!isGenerating && !isInitialLoading && recommendations.length > 0 && (
        <div className="student-recommendations__cards" ref={cardsContainerRef}>
          {recommendations.map((recommendation, index) => (
            <RecommendationCard
              key={recommendation.id || index}
              recommendation={recommendation}
              onClick={handleRecommendationClick}
              containerRef={cardsContainerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
}

StudentRecommendations.propTypes = {
  className: PropTypes.string,
};

StudentRecommendations.defaultProps = {
  className: '',
};

export default StudentRecommendations;
